import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { utmPayload } from "@/lib/utm";
import { useGetStarted } from "@/components/GetStartedDialog";
import PageHead from "@/components/PageHead";
import {
  ShieldAlert, PersonStanding, Gauge, Smartphone,
  AlertTriangle, AlertCircle, Info, CheckCircle2,
  ExternalLink, ChevronDown, ChevronUp,
} from "lucide-react";

const CATEGORY_META = {
  accessibility: { title: "Accessibility", subtitle: "WCAG 2.2 & ADA", Icon: PersonStanding },
  performance:   { title: "Performance",   subtitle: "Speed & weight",  Icon: Gauge },
  mobile:        { title: "Mobile",        subtitle: "Responsiveness",  Icon: Smartphone },
  security:      { title: "Security",      subtitle: "Headers & TLS",   Icon: ShieldAlert },
};

const SEVERITY_META = {
  critical: { label: "Critical", Icon: AlertCircle,   color: "text-brand-red-ink border-brand-red-ink" },
  warning:  { label: "Warning",  Icon: AlertTriangle, color: "text-electric-ink border-electric-ink" },
  info:     { label: "Info",     Icon: Info,          color: "text-label border-[hsl(var(--line-strong))]" },
};

function scoreColor(score) {
  return score >= 60 ? "text-ink" : "text-brand-red-ink";
}

export default function SiteAudit() {
  const [url, setUrl] = useState("");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [expanded, setExpanded] = useState(null);
  const { open } = useGetStarted();
  const navigate = useNavigate();

  const runAudit = async () => {
    setLoading(true); setError(null); setResult(null); setExpanded(null);
    try {
      const { data } = await api.post("/audit", {
        url: url.trim(),
        name: name.trim(),
        email: email.trim().toLowerCase(),
        ...utmPayload(),
      });
      if (!data.ok) {
        setError(data.error || "The audit could not complete.");
        return;
      }
      // Success — route to the confirmation page so Google Ads / other
      // trackers can count this URL as a conversion. The full inline
      // report is delivered by email (audit_email.py).
      navigate("/confirmation?source=audit");
    } catch (e) {
      const status = e?.response?.status;
      const detail = e?.response?.data?.detail;
      if (status === 429 && typeof detail === "string") {
        setError(detail);
      } else if (Array.isArray(detail)) {
        setError(detail.map((d) => d.msg).join(". "));
      } else if (typeof detail === "string") {
        setError(detail);
      } else {
        setError("The audit request failed. Please try again in a moment.");
      }
    } finally { setLoading(false); }
  };

  const isValid =
    url.trim().length > 2 &&
    name.trim().length > 0 &&
    /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());

  const onSubmit = (e) => {
    e.preventDefault();
    if (!isValid) return;
    runAudit();
  };

  return (
    <div data-testid="audit-page">
      <PageHead
        title="Free Website Audit | Accessibility, Speed & SEO Check — Redkey Web Design"
        description="Run a free website audit for WCAG 2.2 accessibility, performance, mobile, and security issues. Get a full report by email from Redkey Web Design."
        canonical="https://redkeyweb.design/audit"
      />
      {/* Header */}
      <section className="mx-auto max-w-[1360px] px-6 md:px-10 pt-16 md:pt-24 pb-10">
        <p className="eyebrow">§ Free Tool · WCAG 2.2 · Core Web Vitals · Security Headers</p>
        <h1 data-testid="audit-title" className="font-display text-5xl md:text-7xl mt-6">
          Website <em className="not-italic text-brand-red">audit.</em>
        </h1>
        <p className="mt-6 max-w-[62ch] text-lg text-ink-soft">
          Paste any public URL. In seconds we check it against{" "}
          <span className="text-ink font-ui">WCAG 2.2</span> accessibility rules,{" "}
          <span className="text-ink font-ui">performance</span> benchmarks,{" "}
          <span className="text-ink font-ui">mobile responsiveness</span>, and key{" "}
          <span className="text-ink font-ui">security headers</span> — then hand you
          the top three problems in plain language, with what they actually cost your business if left broken.
        </p>

        <ul className="mt-8 grid grid-cols-2 md:grid-cols-4 gap-x-8 gap-y-4">
          {Object.entries(CATEGORY_META).map(([k, m]) => {
            const I = m.Icon;
            return (
              <li key={k} className="border-t hairline pt-3">
                <div className="flex items-center gap-2">
                  <I size={16} strokeWidth={1.75} className="text-ink" aria-hidden />
                  <p className="font-ui text-ink text-sm">{m.title}</p>
                </div>
                <p className="mt-1 text-sm text-ink-soft font-body">{m.subtitle}</p>
              </li>
            );
          })}
        </ul>
      </section>

      {/* Form */}
      <section className="mx-auto max-w-[1360px] px-6 md:px-10 border-t hairline pt-10 pb-6">
        <form onSubmit={onSubmit} data-testid="audit-form" className="space-y-6">
          <div>
            <label htmlFor="audit-url" className="block form-label">Site to audit</label>
            <div className="mt-2 flex items-center gap-3 border-b hairline-strong focus-within:border-electric transition-colors">
              <span className="font-ui text-label text-sm hidden sm:inline">https://</span>
              <input
                id="audit-url"
                data-testid="audit-url-input"
                type="text"
                inputMode="url"
                autoComplete="url"
                placeholder="your-website.com"
                value={url}
                onChange={(e) => setUrl(e.target.value)}
                className="flex-1 bg-transparent outline-none py-3 text-lg font-body placeholder:text-label"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label htmlFor="audit-name" className="block form-label">Your name</label>
              <input
                id="audit-name"
                data-testid="audit-name-input"
                type="text"
                autoComplete="name"
                required
                maxLength={120}
                placeholder="First & last name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="mt-2 w-full bg-transparent border-b hairline-strong focus:border-electric outline-none py-3 text-lg font-body placeholder:text-label"
              />
            </div>
            <div>
              <label htmlFor="audit-email" className="block form-label">Where to send the report</label>
              <input
                id="audit-email"
                data-testid="audit-email-input"
                type="email"
                autoComplete="email"
                required
                maxLength={200}
                placeholder="you@company.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="mt-2 w-full bg-transparent border-b hairline-strong focus:border-electric outline-none py-3 text-lg font-body placeholder:text-label"
              />
            </div>
          </div>

          <div className="flex flex-col md:flex-row md:items-center gap-6 pt-2">
            <button
              type="submit"
              disabled={loading || !isValid}
              data-testid="audit-submit"
              className="inline-flex items-center justify-center gap-3 bg-ink text-paper px-6 py-4 hover:bg-electric transition-colors duration-200 disabled:opacity-60"
            >
              <span className="font-ui">{loading ? "Auditing…" : "Get my free audit"}</span>
              <span aria-hidden>→</span>
            </button>
            <p className="font-ui text-[12px] text-label leading-relaxed max-w-[52ch]">
              Free · No credit card · Results in ~10 seconds. We use your email
              only to send this report and follow up personally — no drip
              campaigns, no sharing, and you can opt out any time.
            </p>
          </div>
        </form>
      </section>

      {/* Error */}
      {error && (
        <section className="mx-auto max-w-[1360px] px-6 md:px-10 mt-10" data-testid="audit-error">
          <div className="border hairline-strong bg-paper-alt p-6 md:p-8">
            <p className="eyebrow">Audit could not complete</p>
            <p className="mt-3 font-body text-ink">{error}</p>
          </div>
        </section>
      )}

      {/* Result */}
      {result && (
        <section className="mx-auto max-w-[1360px] px-6 md:px-10 mt-14 space-y-14" data-testid="audit-result">
          {/* Snapshot */}
          <div className="border-y hairline py-8 md:py-10 grid grid-cols-2 md:grid-cols-6 gap-8 items-start">
            <div className="md:col-span-2">
              <p className="eyebrow">Report for</p>
              <p className="mt-2 font-display text-2xl md:text-3xl break-all">
                {result.title || result.final_url}
              </p>
              <a
                href={result.final_url}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-2 inline-flex items-center gap-1.5 font-ui text-sm text-label link-underline"
              >
                {result.final_url} <ExternalLink size={12} />
              </a>
            </div>
            <SummaryStat label="TTFB" value={`${result.fetch_ms} ms`} />
            <SummaryStat label="HTML" value={`${result.html_size_kb} KB`} />
            <SummaryStat label="Compression" value={result.compressed} />
            <SummaryStat label="Issues" value={String(result.summary.total)} />
          </div>

          {/* Category scores strip */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-8">
            {Object.entries(result.categories).map(([slug, cat]) => {
              const meta = CATEGORY_META[slug];
              const Icon = meta?.Icon || Info;
              return (
                <div
                  key={slug}
                  data-testid={`audit-category-${slug}`}
                  className="border hairline-strong p-5"
                >
                  <div className="flex items-center gap-2">
                    <Icon size={18} strokeWidth={1.75} className="text-ink" aria-hidden />
                    <p className="font-ui text-ink">{meta?.title}</p>
                  </div>
                  <div className="mt-3 flex items-baseline gap-3">
                    <p className={`font-display text-4xl md:text-5xl ${scoreColor(cat.score)}`}>{cat.score}</p>
                    <p className="eyebrow">Score</p>
                  </div>
                  <p className="mt-2 font-body text-sm text-ink-soft">
                    {cat.issues.length} {cat.issues.length === 1 ? "issue" : "issues"} found
                  </p>
                </div>
              );
            })}
          </div>

          {/* TOP 3 CRITICAL ISSUES with business consequences */}
          <div data-testid="audit-top-critical">
            <div className="flex items-baseline justify-between gap-6 mb-6">
              <div>
                <p className="eyebrow">§ What we found</p>
                <h2 className="font-display text-3xl md:text-5xl mt-3">
                  {result.top_critical.length === 0
                    ? "Nice — no critical issues."
                    : `The top ${result.top_critical.length} ${result.top_critical.length === 1 ? "problem" : "problems"} to fix first.`}
                </h2>
              </div>
            </div>

            {result.top_critical.length === 0 ? (
              <div className="border hairline-strong p-8 flex items-start gap-4">
                <CheckCircle2 size={22} className="text-[hsl(140_60%_35%)] mt-1" />
                <div>
                  <p className="font-ui text-ink">No critical issues detected in the top-line scan.</p>
                  <p className="mt-2 font-body text-ink-soft">
                    You may still have opportunities in the individual category cards below.
                    Feel free to dig in — or let us do a deeper manual audit if you&rsquo;d like.
                  </p>
                </div>
              </div>
            ) : (
              <ol className="space-y-6">
                {result.top_critical.map((it, i) => {
                  const meta = CATEGORY_META[it.category];
                  const Icon = meta?.Icon || Info;
                  const isOpen = expanded === i;
                  return (
                    <li
                      key={`${it.category}-${it.title}`}
                      data-testid={`audit-top-critical-${i}`}
                      className="border hairline-strong bg-paper"
                    >
                      <div className="p-6 md:p-8">
                        <div className="flex items-start gap-4">
                          <div className="shrink-0 mt-1">
                            <span className="font-display text-4xl md:text-5xl text-brand-red">
                              {String(i + 1).padStart(2, "0")}
                            </span>
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex flex-wrap items-center gap-2">
                              <Icon size={16} strokeWidth={1.75} className="text-label" aria-hidden />
                              <span className="eyebrow">{meta?.title}</span>
                              <span className={`font-ui text-[10px] tracking-widest uppercase border px-1.5 py-0.5 ${SEVERITY_META[it.severity].color}`}>
                                {SEVERITY_META[it.severity].label}
                              </span>
                              {it.wcag && (
                                <span className="font-ui text-[10px] tracking-widest uppercase text-label">
                                  WCAG {it.wcag}
                                </span>
                              )}
                            </div>
                            <h3 className="font-display text-2xl md:text-3xl mt-3">{it.title}</h3>

                            <p className="mt-4 font-display text-xl md:text-2xl text-brand-red-ink leading-snug max-w-[42ch]">
                              {it.consequence_headline}
                            </p>
                            <p className="mt-3 font-body text-ink-soft leading-relaxed max-w-prose">
                              {it.consequence_risk}
                            </p>

                            <div className="mt-4 flex flex-wrap gap-x-4 gap-y-2">
                              {(it.consequence_sources || []).map((s) => (
                                <a
                                  key={s.url}
                                  href={s.url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="inline-flex items-center gap-1 font-ui text-xs text-electric-ink link-underline"
                                >
                                  {s.label} <ExternalLink size={11} />
                                </a>
                              ))}
                            </div>

                            <button
                              type="button"
                              onClick={() => setExpanded(isOpen ? null : i)}
                              data-testid={`audit-critical-toggle-${i}`}
                              className="mt-6 inline-flex items-center gap-2 font-ui text-sm text-ink link-underline"
                              aria-expanded={isOpen}
                            >
                              {isOpen ? (
                                <>Hide the technical detail <ChevronUp size={14} /></>
                              ) : (
                                <>See the technical detail & suggested fix <ChevronDown size={14} /></>
                              )}
                            </button>

                            {isOpen && (
                              <div className="mt-4 pl-4 border-l border-brand-red">
                                <p className="font-body text-ink leading-relaxed">
                                  {it.detail}
                                </p>
                                {it.fix && (
                                  <p className="mt-3 font-body text-ink-soft leading-relaxed">
                                    <span className="eyebrow mr-2">Fix</span>
                                    <span className="text-ink">{it.fix}</span>
                                  </p>
                                )}
                                <p className="mt-4 font-body text-ink-soft leading-relaxed">
                                  <span className="eyebrow mr-2">If we fix this</span>
                                  <span className="text-ink">{it.consequence_upside}</span>
                                </p>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </li>
                  );
                })}
              </ol>
            )}

            {/* CTA — the "would you like to fix these?" ask */}
            {result.top_critical.length > 0 && (
              <div className="mt-10 border hairline-strong bg-ink text-paper p-8 md:p-10" data-testid="audit-cta-panel">
                <p className="font-ui text-[11px] tracking-widest text-paper/60">§ Next step</p>
                <h3 className="font-display text-3xl md:text-5xl mt-4 text-paper max-w-[28ch]">
                  Want Redkey Web Design to fix these for you?
                </h3>
                <p className="mt-4 font-body text-paper/85 max-w-prose">
                  We remediate accessibility, performance, mobile, and security issues
                  for U.S. businesses every day — usually as a fixed-price engagement,
                  usually in 2–4 weeks. No lock-in, no jargon, no upsell.
                </p>
                <div className="mt-6 flex flex-wrap gap-3">
                  <button
                    type="button"
                    onClick={open}
                    data-testid="audit-cta-fix"
                    className="inline-flex items-center gap-3 bg-brand-red text-paper px-6 py-4 hover:bg-ink transition-colors font-ui"
                  >
                    Yes, let&rsquo;s fix these problems <span aria-hidden>→</span>
                  </button>
                  <a
                    href="#audit-details"
                    className="inline-flex items-center gap-3 border border-paper/40 text-paper px-6 py-4 hover:border-paper transition-colors font-ui"
                  >
                    First, show me the full report
                  </a>
                </div>
              </div>
            )}
          </div>

          {/* Full category detail (below the fold) */}
          <div id="audit-details">
            <p className="eyebrow">§ Full report</p>
            <h2 className="font-display text-3xl md:text-5xl mt-3 mb-6">All findings by category.</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
              {Object.entries(result.categories).map(([slug, cat]) => (
                <CategoryCard key={slug} slug={slug} category={cat} />
              ))}
            </div>
          </div>

          <p className="font-body text-sm text-label max-w-prose">
            <strong className="text-ink font-ui">A note on this audit.</strong>{" "}
            This is a fast static scan of your HTML and response headers. It catches
            issues you can fix today, but it isn&rsquo;t a substitute for a full
            manual accessibility review, a real Lighthouse run with rendered JS,
            or a penetration test. Treat it as a starting checklist, not a
            certification.
          </p>
        </section>
      )}
    </div>
  );
}

function SummaryStat({ label, value }) {
  return (
    <div>
      <p className="eyebrow">{label}</p>
      <p className="mt-2 font-display text-2xl md:text-3xl">{value}</p>
    </div>
  );
}

function CategoryCard({ slug, category }) {
  const meta = CATEGORY_META[slug];
  const Icon = meta?.Icon || Info;
  return (
    <article
      data-testid={`audit-category-detail-${slug}`}
      className="border hairline-strong bg-paper p-6 md:p-8"
    >
      <header className="flex items-start justify-between gap-6">
        <div className="flex items-center gap-3">
          <Icon size={22} strokeWidth={1.75} className="text-ink" aria-hidden />
          <div>
            <h3 className="font-display text-2xl md:text-3xl">{meta?.title || slug}</h3>
            <p className="eyebrow mt-1">{meta?.subtitle}</p>
          </div>
        </div>
        <div className="text-right">
          <p className={`font-display text-4xl md:text-5xl ${scoreColor(category.score)}`}>{category.score}</p>
          <p className="eyebrow mt-1">Score</p>
        </div>
      </header>
      <div className="mt-6 space-y-4">
        {category.issues.length === 0 ? (
          <div className="flex items-center gap-2 text-ink-soft font-body">
            <CheckCircle2 size={16} className="text-[hsl(140_60%_35%)]" />
            No issues detected in this category.
          </div>
        ) : (
          category.issues.map((issue, i) => <IssueRow key={`${issue.severity}-${issue.title}-${i}`} issue={issue} />)
        )}
      </div>
    </article>
  );
}

function IssueRow({ issue }) {
  const sev = SEVERITY_META[issue.severity] || SEVERITY_META.info;
  const SevIcon = sev.Icon;
  return (
    <div className="border-t hairline pt-4">
      <div className="flex items-start gap-3">
        <SevIcon size={16} className={sev.color.split(" ")[0]} strokeWidth={2} />
        <div className="flex-1 min-w-0">
          <div className="flex flex-wrap items-baseline gap-2">
            <p className="font-ui text-base text-ink">{issue.title}</p>
            <span className={`font-ui text-[10px] tracking-widest uppercase border px-1.5 py-0.5 ${sev.color}`}>
              {sev.label}
            </span>
            {issue.wcag && (
              <span className="font-ui text-[10px] tracking-widest uppercase text-label">
                WCAG {issue.wcag}
              </span>
            )}
          </div>
          <p className="mt-2 font-body text-[15px] text-ink-soft leading-relaxed">
            {issue.detail}
          </p>
          {issue.fix && (
            <p className="mt-2 font-body text-[15px]">
              <span className="eyebrow mr-2">Fix</span>
              <span className="text-ink">{issue.fix}</span>
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
